import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  structuredData?: object | object[];
}

export const SEOHead = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = "https://www.feelfreeholidays.com/og-image.jpg",
  ogType = "website",
  structuredData,
}: SEOHeadProps) => {
  const location = useLocation();
  const fullTitle = title.includes("Feel Free Holidays") ? title : `${title} | Feel Free Holidays`;
  const baseUrl = "https://www.feelfreeholidays.com";

  // Auto-generate a clean canonical URL per route.
  // - Strip query strings and hash fragments
  // - Lowercase the path (canonicals are case-sensitive to crawlers)
  // - Remove trailing slash (except root)
  // An explicit `canonicalUrl` prop overrides the auto value when needed.
  const normalizePath = (pathname: string) => {
    const lower = pathname.toLowerCase();
    if (lower === "/" || lower === "") return "";
    return lower.replace(/\/+$/, "");
  };
  const canonical = canonicalUrl || `${baseUrl}${normalizePath(location.pathname)}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content="Feel Free Holidays" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@feelfreeholidays" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={fullTitle} />

      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="author" content="Feel Free Holidays" />
      <meta name="geo.region" content="IN-GJ" />
      <meta name="geo.placename" content="Surat" />

      {/* Structured Data — emit each schema in its own <script> tag for best parsing */}
      {structuredData && (
        Array.isArray(structuredData) ? (
          structuredData.map((schema, i) => (
            <script
              key={i}
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
            />
          ))
        ) : (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          />
        )
      )}
    </Helmet>
  );
};

// Reusable structured data generators
export const generateLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "TravelAgency",
  "name": "Feel Free Holidays",
  "alternateName": "Best Travel Agency in Surat",
  "image": "https://www.feelfreeholidays.com/og-image.jpg",
  "url": "https://www.feelfreeholidays.com",
  "telephone": "+91-7490019959",
  "email": "info@feelfreeholidays.com",
  "address": {
    "@type": "PostalAddress",
   "streetAddress": "18th Floor, The Junomoneta Tower, 1806, Opp. Pal RTO, Near Rajhans Cinema, Pal, Adajan",
    "addressLocality": "Surat",
    "addressRegion": "Gujarat",
    "postalCode": "394510",
    "addressCountry": "IN"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "21.1702",
    "longitude": "72.8311"
  },
  "areaServed": {
    "@type": "City",
    "name": "Surat",
    "containedInPlace": {
      "@type": "State",
      "name": "Gujarat"
    }
  },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    "opens": "10:00",
    "closes": "19:00"
  },
  "sameAs": [
    "https://instagram.com/feelfree.holidays",
    "https://www.google.com/maps/place/Feel+Free+Holidays/@21.1835438,72.78583,17z"
  ],
  "priceRange": "$$",
  "description": "Feel Free Holidays is the best travel agency in Surat and leading tour operators in Adajan. We specialize in custom international tours, visa assistance, honeymoon packages, and family vacations from Gujarat.",
  "keywords": "best travel agency in Surat, tour operators in Adajan, visa agent Surat, international tour packages from Surat"
});

export const generateBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

export const generateFAQSchema = (faqs: { question: string; answer: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

/**
 * Sitewide WebSite schema with SearchAction (sitelinks search box).
 * Render once on the home page.
 */
export const generateWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Feel Free Holidays",
  "alternateName": "Feel Free Holidays — Best Travel Agency in Surat",
  "url": "https://www.feelfreeholidays.com",
  "publisher": {
    "@type": "TravelAgency",
    "name": "Feel Free Holidays",
    "logo": {
      "@type": "ImageObject",
      "url": "https://www.feelfreeholidays.com/og-image.jpg"
    }
  },
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://www.feelfreeholidays.com/destinations?q={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  }
});

/**
 * Generates a TouristTrip / Product-style schema for a destination listing page.
 */
export const generateTouristTripSchema = (input: {
  name: string;
  description: string;
  image: string;
  url: string;
  duration?: string;
  bestTime?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "TouristTrip",
  "name": input.name,
  "description": input.description,
  "image": input.image,
  "url": input.url,
  ...(input.duration ? { "estimatedDuration": input.duration } : {}),
  ...(input.bestTime ? { "subjectOf": { "@type": "CreativeWork", "name": `Best time to visit ${input.name}`, "text": input.bestTime } } : {}),
  "provider": {
    "@type": "TravelAgency",
    "name": "Feel Free Holidays",
    "url": "https://www.feelfreeholidays.com"
  }
});
